/**
 * v4c-recommendations.js — V4C recommended-for-you カルーセル / グリッド。
 *
 * <v4c-recommendations> custom element。V4cClient.recommend() でパーソナライズ
 * された productId 配列を取得し、Horizon native の <product-card> を
 *   - data-layout="carousel": 横スクロールトラック (最近見た商品と同形)
 *   - data-layout="grid":     グリッド (コレクション相当ページ用)
 * に描画する。
 *
 * 描画経路は検索結果と共通: Section Rendering API
 * `/search?q=id:... &sections=v4c-search-batch` で <product-card> HTML を取得し、
 * V4C ランキング順に並べる。country/locale を付けるので Markets / 多通貨に追従する。
 *
 * grid は無限スクロール: recommend() で ID を一括で取り切り (Retail API Predict は
 * ページネーション非対応・最大 100 件)、カード HTML は BATCH_SIZE 件ずつ
 * IntersectionObserver で必要になったタイミングで section render して追記する。
 * carousel は従来どおり一括描画 (件数が少なく sentinel も置けないため)。
 *
 * モデル学習中 / cold-start / 障害時は結果が空になりうる。その場合はセクション
 * ごと非表示にして崩れを防ぐ (theme editor の design mode では確認のため表示維持)。
 */
(function () {
  'use strict';

  if (customElements.get('v4c-recommendations')) return;

  // 1 回の section render で取得・追記するカード数。v4c-search-batch 側の
  // `paginate by 50` と `/search` の q パラメータ長を考慮して控えめに保つ。
  var BATCH_SIZE = 24;

  function publish(eventName, payload) {
    try {
      if (
        window.Shopify &&
        window.Shopify.analytics &&
        typeof window.Shopify.analytics.publish === 'function'
      ) {
        window.Shopify.analytics.publish(eventName, payload);
      }
    } catch (e) {
      /* noop */
    }
  }

  // productId 配列 → Section Rendering API で <product-card> HTML を取得
  async function fetchCardsHtml(productIds) {
    if (!productIds.length) return '';
    var idQuery = productIds
      .map(function (id) { return 'id:' + id; })
      .join(' OR ');
    var base =
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.search_url) ||
      '/search';
    var params = new URLSearchParams();
    params.set('q', idQuery);
    params.set('sections', 'v4c-search-batch');
    if (window.Shopify && window.Shopify.country) {
      params.set('country', window.Shopify.country);
    }
    if (window.Shopify && window.Shopify.locale) {
      params.set('locale', window.Shopify.locale);
    }
    var res = await fetch(base + '?' + params.toString(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) return '';
    var json = await res.json();
    return json['v4c-search-batch'] || '';
  }

  class V4cRecommendations extends HTMLElement {
    connectedCallback() {
      this.limit = parseInt(this.getAttribute('data-limit'), 10) || 12;
      this.layout = this.getAttribute('data-layout') || 'carousel';
      this.designMode = this.getAttribute('data-design-mode') === 'true';
      this.list = this.querySelector('[data-v4c-reco-list]');
      this.skeleton = this.querySelector('[data-v4c-reco-skeleton]');
      this.productIds = [];
      this.renderedCount = 0;
      this.loading = false;
      if (!this.list || !window.V4cClient) {
        this.handleEmpty();
        return;
      }
      this.run();
    }

    disconnectedCallback() {
      this.teardownInfiniteScroll();
    }

    async run() {
      try {
        // include_section は使わない。商品カードは client 側で country/locale を
        // 付けて section render する (Markets 対応。検索結果と同経路)。
        var data = await window.V4cClient.recommend({ limit: this.limit });
        this.productIds = (data.products || [])
          .map(function (p) { return p.productId; })
          .filter(Boolean);
        if (!this.productIds.length) {
          this.handleEmpty();
          return;
        }
        var appended = await this.renderNextBatch();
        if (!appended) {
          this.handleEmpty();
          return;
        }
        this.publishCompleted(data, this.productIds);
        if (this.layout === 'grid') {
          this.setupInfiniteScroll();
        }
      } catch (e) {
        console.error('[v4c-recommendations] failed', e);
        this.handleEmpty();
      }
    }

    /**
     * 次の BATCH_SIZE 件をカード HTML 化して追記する。
     * @returns {Promise<number>} 追記できたカード数 (0 = 打ち止め)
     */
    async renderNextBatch() {
      var next = this.productIds.slice(
        this.renderedCount,
        this.renderedCount + BATCH_SIZE
      );
      // 消費済み扱いにしてから fetch する (ヒットしない ID が残留してもループしない)
      this.renderedCount += next.length;
      if (!next.length) return 0;
      var html = await fetchCardsHtml(next);
      if (!html) return 0;

      var tpl = document.createElement('template');
      tpl.innerHTML = html.trim();
      var byId = new Map();
      tpl.content.querySelectorAll('product-card').forEach(function (c) {
        var pid = c.dataset.productId;
        if (pid) byId.set(pid, c);
      });
      var frag = document.createDocumentFragment();
      var appended = 0;
      for (var i = 0; i < next.length; i++) {
        var card = byId.get(next[i]);
        if (!card) continue;
        var li = document.createElement('li');
        li.className = 'v4c-reco__cell';
        li.appendChild(card);
        frag.appendChild(li);
        appended += 1;
      }
      if (appended) {
        this.removeSkeleton();
        this.list.appendChild(frag);
        this.list.hidden = false;
      }
      return appended;
    }

    // 初期ロード用スケルトン (section 側で server render) を取り除く。
    // 実カードの初回描画時と、空応答でセクションを畳むときの両方から呼ばれる
    removeSkeleton() {
      if (this.skeleton) {
        this.skeleton.remove();
        this.skeleton = null;
      }
      this.removeAttribute('aria-busy');
    }

    get hasMore() {
      return this.renderedCount < this.productIds.length;
    }

    // ---- 無限スクロール (grid のみ) ----
    setupInfiniteScroll() {
      if (!this.hasMore || !('IntersectionObserver' in window)) return;
      var sentinel = document.createElement('div');
      sentinel.className = 'v4c-reco__sentinel';
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.innerHTML = '<span class="v4c-reco__spinner"></span>';
      this.appendChild(sentinel);
      this.sentinel = sentinel;

      var self = this;
      this.observer = new IntersectionObserver(
        function (entries) {
          if (!entries.some(function (e) { return e.isIntersecting; })) return;
          self.loadMore();
        },
        // 下端の 800px 手前で先読みして体感待ちを減らす
        { rootMargin: '0px 0px 800px 0px' }
      );
      this.observer.observe(sentinel);
    }

    async loadMore() {
      if (this.loading || !this.hasMore) return;
      this.loading = true;
      if (this.sentinel) this.sentinel.classList.add('is-loading');
      try {
        await this.renderNextBatch();
      } catch (e) {
        console.error('[v4c-recommendations] load more failed', e);
        // 追記失敗時は打ち止め (連続リトライでの無限ループを避ける)
        this.renderedCount = this.productIds.length;
      } finally {
        this.loading = false;
        if (this.sentinel) this.sentinel.classList.remove('is-loading');
      }
      if (!this.hasMore) {
        this.teardownInfiniteScroll();
        return;
      }
      // 追記後も sentinel が viewport 内に残っている場合、IntersectionObserver は
      // 状態変化しない限り再発火しない。observe し直して現在の交差状態を再通知させる。
      if (this.observer && this.sentinel) {
        this.observer.unobserve(this.sentinel);
        this.observer.observe(this.sentinel);
      }
    }

    teardownInfiniteScroll() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.sentinel) {
        this.sentinel.remove();
        this.sentinel = null;
      }
    }

    // attributionToken を pixel へ。検索の v4c_search_completed と同じ要領で
    // localStorage 経由 (client が persist 済) + custom event の二経路。
    publishCompleted(data, productIds) {
      var ids = (data.products || [])
        .slice(0, 24)
        .map(function (p) { return { id: p.v4cId }; })
        .filter(function (x) { return x.id; });
      publish('v4c_recommend_completed', {
        productDetails: ids,
        totalSize: productIds.length,
        source: data.source || '',
        attributionToken: data.attributionToken || '',
      });
    }

    handleEmpty() {
      this.removeSkeleton();
      this.teardownInfiniteScroll();
      // おすすめが空 / 障害時は見出しを含むセクション全体を非表示にする
      // (崩れ防止)。ただし theme editor の design mode では merchant が
      // 見出しブロックを設定できるよう表示維持。
      if (this.designMode) return;
      var root = this.closest('[data-v4c-reco-root]') || this;
      root.hidden = true;
    }
  }

  customElements.define('v4c-recommendations', V4cRecommendations);
})();
